import type { CheckinMember } from '../members';
import { textOnMemberColor } from '../../../theme/memberColor';

/**
 * Member avatar (design: "Component — MemberChip", sizes 20/24/28/32/36/44).
 *
 * Colour never carries meaning on its own — the initial or the member's chosen glyph
 * always rides along, so the feed reads the same to someone who can't tell ember from
 * plum.
 */

const SIZES = {
  20: 'h-5 w-5 text-[10px]',
  24: 'h-6 w-6 text-[11px]',
  28: 'h-7 w-7 text-[12px]',
  32: 'h-8 w-8 text-[13px]',
  36: 'h-9 w-9 text-[14px]',
  44: 'h-11 w-11 text-[16px]',
} as const;

export function Avatar({
  member,
  size = 32,
  ring = false,
}: {
  member: CheckinMember;
  size?: keyof typeof SIZES;
  /** A 2px surface ring, for the overlapping "seen by" stack. */
  ring?: boolean;
}): JSX.Element {
  // "No profile" variant from the design package: neutral fill, dashed outline. Used until
  // the household roster exists, so an unnamed member never borrows someone's colour.
  const paint = member.unknown ? 'bg-raised text-fg-3 border border-dashed border-line-strong' : '';
  const style = member.unknown
    ? undefined
    : { backgroundColor: member.color, color: textOnMemberColor(member.color) };

  return (
    <span
      style={style}
      className={`flex flex-shrink-0 items-center justify-center rounded-full font-bold ${SIZES[size]} ${paint} ${ring && !member.unknown ? 'border-2 border-surface' : ''}`}
      title={member.name}
      aria-label={member.name}
    >
      {member.avatarEmoji ?? member.initial}
    </span>
  );
}
